/**
 *      ▄ █
 *    █ █ ▀▀█  LOADBALANCER CLIENT
 *    █ ▀ ▀ █  @copyright Copyright (c) 2025
 */
import { Component, ElementRef, Input, OnInit, TemplateRef, ViewChild } from '@angular/core';
import {
    ContentInjectionService,
    LanguageService,
    LogService,
    ModalService,
    NavigationService,
    RbacService
} from '../../services';
import { Router } from '@angular/router';
import { UsageModel } from '../../models';

@Component({
    selector: 'app-usage',
    templateUrl: './usage.component.html',
    styleUrls: ['./usage.component.scss']
})
export class UsageComponent implements OnInit {
    /**
     * Container for usage text.
     */
    @ViewChild('usageContainer', { static: true })
    public usageContainer: ElementRef;

    /**
     * Container of the tooltip to show.
     */
    @ViewChild('tooltipContainer', { static: true })
    public tooltipContainer: TemplateRef<any>;

    /**
     * Usage details from parent
     */
    @Input() set usage(usage: UsageModel[]) {
        this._usage = usage;
    }

    /**
     * Modal title.
     */
    @Input() public modalTitle: string = '';

    /**
     * Modal subtitle.
     */
    @Input() public modalSubTitle: string = '';

    /**
     * Table headers.
     */
    @Input() public columnHeaders: string[] = ['service', 'section', 'services_name'];

    /**
     * Table row property to use for the value in each row.
     */
    @Input() public rowProperties: string[] = ['serviceFullName', 'sectionFullName', 'serviceName'];

    /**
     * Usage Modal Ip.
     */
    public modalIp: string = '';

    /**
     * Tooltip open flag.
     */
    public tooltipOpen: boolean = false;

    /**
     * Store button permissions.
     */
    public permissionsLookup: any = {};

    /**
     * Private variable for setter and getter.
     * @private
     */
    private _usage: UsageModel[] = [];

    constructor(
        private contentInjectionService: ContentInjectionService,
        private languageService: LanguageService,
        private logService: LogService,
        private modalService: ModalService,
        private navigationService: NavigationService,
        private rbacService: RbacService,
        private router: Router
    ) {
        this.subscribeToContentInjectionCloseEmitter();
        this.registerButtons();
    }

    ngOnInit() {
        if (this.columnHeaders?.length !== this.rowProperties?.length) {
            this.logService.debug('UsageComponent:ngOnInit', 'Mismatch in columns and rowProperties');
        }
    }

    get usage() {
        return this._usage;
    }

    /**
     * Get usage message.
     * @param usage
     */
    public getUsageMessage(usage: any): string {
        let message: string;
        if (usage?.length > 1) {
            // eslint-disable-next-line max-len
            message = `${this.languageService.translate('usage_used_in')} ${usage.length} ${this.languageService.translate('places')}`;
        } else if (usage?.length === 1) {
            // eslint-disable-next-line max-len
            message = `${this.languageService.translate('usage_used_in')} ${usage.length} ${this.languageService.translate('place')}`;
        } else {
            message = this.languageService.translate('usage_no_usage');
        }

        return message;
    }

    /**
     * Link from usage info.
     * @param link
     */
    public linkToServiceDetails(link: string): void {
        this.router.navigateByUrl(this.navigationService.getEnduranceUrl(link)).then().catch();
    }

    /**
     * Show usage modal.
     */
    public showUsageModal(): void {
        this.modalService.openModalInParent('usageModal');
    }

    /**
     * Subscribe to content injection close emitter
     * @private
     */
    private subscribeToContentInjectionCloseEmitter(): void {
        this.contentInjectionService.closeEmitter
            .subscribe({
                next: (closed: boolean): void => {
                    if (closed) {
                        this.tooltipOpen = false;
                    }
                }
            });
    }

    /**
     * Register button permissions.
     * @private
     */
    private registerButtons(): void {
        const buttonReference = {
            'usage-service-details-link': [
                { path: '/haproxy/frontends/:id', methods: ['GET'] },
                { path: '/haproxy/frontends/:id/backends', methods: ['GET'] },
                { path: '/fips', methods: ['GET'] },
            ]
        };

        this.permissionsLookup = this.rbacService.getPermissionsLookup(buttonReference);
    }
}
